import React, { useEffect, useEffectEvent, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { Modal, ModalBody } from '@cfa/react-components';

import useGetStructuredBrowseData, {
  UnkeyedCategory,
  UnkeyedDocument,
  UnkeyedSubcategory,
} from '@/hooks/useGetStructuredBrowseData';

import { isApiError } from '@/utils/request';
import { cn } from '@/utils/tailwind';

import ActivityStepWizard from '@/components/ActivityStepWizard/ActivityStepWizard';
import DocumentPreview from '@/components/DocumentPreview/DocumentPreview';
import GenericError from '@/components/Error/GenericError';
import ScrollModalHeader from '@/components/ScrollModal/ScrollModalHeader';

import { AddProcedureFooter } from './components/AddProcedureFooter';
import { AddProcedureHeader } from './components/AddProcedureHeader';
import StepOneCategories from './components/StepOneCategories';
import StepThreeSearchResults from './components/StepThreeSearchResults';
import StepTwoSubcategories from './components/StepTwoSubcategories';
import { PROCEDURE_STEPS } from './constants';
import { PopupDocument } from './types';

import { IntlSubcategoryDto } from '@/_generated/api-clients/content';
import {
  BaseReferenceDto,
  SearchDocument as Document,
} from '@/_generated/api-clients/xp';
import { Plan } from '@/_generated/api-clients/xp';
import { useSearchDispatch } from '@/atoms/search';
import { useProcedureState } from '@/routes/TrainingPlans/BuildPlans/hooks/useProcedureState';

export type ProcedureStep =
  (typeof PROCEDURE_STEPS)[keyof typeof PROCEDURE_STEPS];

interface AddProcedurePopupProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: Plan;
  isInSection?: boolean;
  sectionId?: string;
  onCustomAddProcedure?: (selectedDocuments: PopupDocument[]) => void;
  adminAddingProcedure?: boolean;
  selectedCountryCode?: string; // country code value will only exist if imported from Admin component and is Admin user
  onlyAllowOneDoc?: boolean;
  textVariant?: 'update' | 'default';
}

const createStepKey = (
  stepId: number,
  selections: Record<string, unknown>,
): string => {
  const keys = Object.keys(selections)
    .filter(k => selections[k] != null)
    .sort();

  const parts = [`step:${stepId}`];
  for (const k of keys) {
    let v = selections[k];
    if (typeof v === 'boolean') v = v ? 1 : 0;
    parts.push(`${k}:${String(v)}`);
  }
  return parts.join('|');
};

const AddProcedurePopup: React.FC<AddProcedurePopupProps> = ({
  isOpen,
  onClose,
  plan,
  isInSection = false,
  sectionId = '',
  onCustomAddProcedure,
  adminAddingProcedure = false,
  selectedCountryCode,
  onlyAllowOneDoc = false,
  textVariant = 'default',
}) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const searchDispatch = useSearchDispatch();

  const planId = plan?.id;

  // Draft persistence - all persisted state managed by hook
  const {
    activeStep,
    setActiveStep,
    search,
    setSearch,
    selectedDocuments,
    setSelectedDocuments,
    categorySelected,
    setCategorySelected,
    categoryName,
    setCategoryName,
    preservedSubcategoryIds,
    setPreservedSubcategoryIds,
    previewDocument,
    setPreviewDocument,
    resetDraft,
  } = useProcedureState({
    isOpen,
    planId,
    sectionId,
    isInSection,
  });

  // Local UI state (not persisted)
  const [isTridionPreview, setIsTridionPreview] = useState(false);
  const [subcategories, setSubcategories] = useState<
    UnkeyedSubcategory[] | IntlSubcategoryDto[] | null
  >(null);
  const [browseCategories, setBrowseCategories] = useState<
    UnkeyedCategory[] | null
  >(null);

  // Derived values
  const isPreviewResource = activeStep === PROCEDURE_STEPS.PREVIEW;

  const stepData = (() => {
    const selections: Record<string, string | undefined> = {};
    const category = categorySelected as UnkeyedCategory | null;

    if (activeStep === PROCEDURE_STEPS.SUBCATEGORIES && category?.id) {
      selections.categoryId = category.id;
    }
    if (activeStep === PROCEDURE_STEPS.SEARCH && search.query) {
      selections.query = search.query.trim().toLowerCase();
    }
    if (
      activeStep === PROCEDURE_STEPS.PREVIEW &&
      'id' in previewDocument &&
      typeof previewDocument.id === 'string'
    ) {
      selections.documentId = previewDocument.id;
    }
    return selections;
  })();

  const categories = browseCategories
    ? browseCategories.filter(category => !category.featured)
    : [];

  const featured = browseCategories
    ? browseCategories.filter(category => category.featured)
    : [];

  // Action handlers
  const handleSearchTermSelect = (
    suggestion: string | null,
    spellingSuggestion?: string,
  ) => {
    const termToUse =
      spellingSuggestion ?? suggestion ?? search.displayed.trim();
    if (termToUse.length) {
      setSearch({
        ...search,
        displayed: termToUse,
        query: termToUse,
        misspelling: spellingSuggestion ?? '',
      });
      setActiveStep(PROCEDURE_STEPS.SEARCH);
    }
  };

  const handleBackButtonClick = (onCloseCallback?: () => void) => {
    if (search.query && activeStep === PROCEDURE_STEPS.SEARCH) {
      setSearch({ ...search, query: '' });
      setActiveStep(PROCEDURE_STEPS.CATEGORIES);
      return;
    }
    if (search.query && activeStep === PROCEDURE_STEPS.PREVIEW) {
      setActiveStep(PROCEDURE_STEPS.SEARCH);
      return;
    }
    if (!search.query && activeStep === PROCEDURE_STEPS.PREVIEW) {
      setActiveStep(PROCEDURE_STEPS.SUBCATEGORIES);
      return;
    }
    if (activeStep > PROCEDURE_STEPS.CATEGORIES) {
      setActiveStep((activeStep - 1) as ProcedureStep);
    } else {
      onCloseCallback?.();
    }
  };

  // Use useEffectEvent for effects that need latest values without re-runs
  const handleResetSearch = useEffectEvent(() => {
    searchDispatch.reset();
  });

  const handleSetBrowseCategories = useEffectEvent(
    (categories: UnkeyedCategory[] | null) => {
      setBrowseCategories(categories);
    },
  );

  useEffect(() => {
    handleResetSearch();
  }, []);

  const { data: browseCategoriesData, error: browseError } =
    useGetStructuredBrowseData({
      structure: 'unkeyed',
    });

  // Update browseCategories when data changes
  useEffect(() => {
    handleSetBrowseCategories(browseCategoriesData ?? null);
  }, [browseCategoriesData]);

  const handleOnClose = () => {
    onClose();
    searchDispatch.reset();
    setIsTridionPreview(false);
    setSubcategories(null);
    resetDraft();
  };

  const scrollMemoryKey = createStepKey(activeStep, stepData);

  if (isApiError(browseError)) {
    datadogRum.addError(browseError, { section: 'browseCategories' });
    return <GenericError />;
  }

  return (
    <Modal
      onClose={handleOnClose}
      scrollMode="modal-body"
      show={isOpen}
      size="lg"
      data-testid={
        textVariant === 'update' ? 'UpdateResourceModal' : 'AddResourceModal'
      }
      className="relative"
    >
      <ScrollModalHeader
        scrollRef={modalBodyRef}
        showCloseButton={!isPreviewResource}
        className="pt-12! pb-4!"
      >
        <AddProcedureHeader
          activeStep={activeStep}
          searchTermDisplayed={search.displayed}
          setSearchTermDisplayed={value =>
            setSearch({ ...search, displayed: value })
          }
          previewDocument={previewDocument}
          isPreviewResource={isPreviewResource}
          onSearchTermSelect={handleSearchTermSelect}
          handleBackButtonClick={handleBackButtonClick}
          onClose={handleOnClose}
        />
      </ScrollModalHeader>
      <ModalBody
        ref={modalBodyRef}
        className="overflow-y-auto!"
        style={{
          background: isPreviewResource
            ? 'var(--documentBackground)'
            : 'transparent',
        }}
      >
        <div
          className={cn(
            'text-left',
            activeStep === PROCEDURE_STEPS.PREVIEW && isTridionPreview
              ? 'my-0'
              : 'my-4',
          )}
        >
          <ActivityStepWizard
            activeStep={activeStep}
            containerRef={modalBodyRef}
            scrollMemory={{
              container: modalBodyRef,
              key: scrollMemoryKey,
            }}
          >
            {/* Step 1: Categories */}
            <StepOneCategories
              categories={categories}
              featured={featured}
              setCategorySelected={
                setCategorySelected as (
                  category: UnkeyedCategory | null,
                ) => void
              }
              setSearchTermDisplayed={value =>
                setSearch({ ...search, displayed: value })
              }
              setActiveStep={setActiveStep}
            />
            {/* Step 2: Subcategories */}
            <StepTwoSubcategories
              categorySelected={categorySelected as UnkeyedCategory | null}
              preservedSubcategoryIds={preservedSubcategoryIds}
              setPreservedSubcategoryIds={setPreservedSubcategoryIds}
              subcategories={subcategories}
              categoryName={categoryName}
              setSubcategories={setSubcategories}
              setCategoryName={setCategoryName}
              setPreviewDocument={
                setPreviewDocument as (
                  doc: Document | BaseReferenceDto | UnkeyedDocument,
                ) => void
              }
              setActiveStep={setActiveStep}
              selectedCountryCode={selectedCountryCode}
              adminAddingProcedure={adminAddingProcedure}
              selectedDocuments={selectedDocuments}
              setSelectedDocuments={setSelectedDocuments}
              onlyAllowOneDoc={onlyAllowOneDoc}
            />
            {/* Step 3: Search Results */}
            <StepThreeSearchResults
              searchTermQuery={search.query}
              setPreviewDocument={
                setPreviewDocument as (
                  doc: Document | BaseReferenceDto | UnkeyedDocument,
                ) => void
              }
              setActiveStep={setActiveStep}
              misspellingSuggestion={search.misspelling}
              setMisspellingSuggestion={value =>
                setSearch({ ...search, misspelling: value })
              }
              noMoreResults={search.noMoreResults}
              setNoMoreResults={value =>
                setSearch({ ...search, noMoreResults: value })
              }
              onSearchTermSelect={handleSearchTermSelect}
              selectedCountryCode={selectedCountryCode}
              selectedDocuments={selectedDocuments}
              setSelectedDocuments={setSelectedDocuments}
              onlyAllowOneDoc={onlyAllowOneDoc}
            />
            {/* Step 4: Document Preview */}
            <DocumentPreview
              previewDocument={previewDocument}
              handleGoBack={() => handleBackButtonClick(onClose)}
            />
          </ActivityStepWizard>
        </div>
      </ModalBody>
      <AddProcedureFooter
        scrollRef={modalBodyRef}
        plan={plan}
        isInSection={isInSection}
        sectionId={sectionId}
        adminAddingProcedure={adminAddingProcedure}
        onClose={handleOnClose}
        selectedDocuments={selectedDocuments}
        setSelectedDocuments={setSelectedDocuments}
        isPreviewResource={isPreviewResource}
        onCustomAddProcedure={onCustomAddProcedure}
        textVariant={'update'}
      />
    </Modal>
  );
};

export default AddProcedurePopup;
